import React, { useState } from 'react';

interface Topic {
  title: string;
  icon: string;
  description: string;
  details: string[];
}

const topics: Topic[] = [
  {
        title: "Dissertation",
        icon: "📚",
        description: "Our company offers expert assistance for dissertation writing, including topic selection, research guidance, and thorough Project writing services.",
        details: [
          "Our expert team provides comprehensive dissertation writing services, covering topic selection, research methodology, and editing."
        ]
      },
      {
        title: "Coursework",
        icon: "📝",
        description: "Our company provides comprehensive coursework support, from topic selection to final submission. We offer research assistance....",
        details: [
          "Our team offers end-to-end coursework support, including topic selection, research assistance, writing, and final edits. We ensure that every aspect of your coursework meets academic requirements and is delivered on time. Our personalized approach ensures you get the help you need to succeed."
        ]
      },
      {
        title: "Data Analysis",
        icon: "🔍",
        description: "Our company specializes in data analysis services, offering expert support in collecting, processing, and interpreting data for academic projects....",
        details: [
          "We offer specialized data analysis services for academic research, using advanced statistical tools to collect, process, and interpret data. Our experts provide clear and actionable insights, helping you make data-driven decisions that enhance the quality and credibility of your research."
        ]
      },
      {
        title: "Project Writing Services",
        icon: "✍️",
        description: "Our company offers professional Project writing services to refine your academic work with precision. We ensure clarity, coherence, and adherence to academic standards....",
        details: [
          "Our Project writing services focus on improving the clarity, structure, and coherence of your work. We ensure that your academic writing adheres to the highest standards, correcting grammar, formatting, and citation errors. Our team helps you deliver a polished and professional final submission."
        ]
      },
      {
        title: "Publication",
        icon: "📑",  // Changed from thinking face to document icon
        description: "Our company provides tailored Publication services designed to enhance your academic skills, covering areas such as research methods, writing techniques....",
        details: [
          "Our Publication services are designed to help you develop the necessary skills to publish your research. From writing techniques to advanced....."
        ]
      },
      {
        title: "Literature Review",
        icon: "📖",
        description: "Our company offers specialized literature review services, helping you identify, analyze, and synthesize key academic sources relevant to your research....",
        details: [
          "Our Literature Review services help you gather and analyze key academic sources relevant to your research topic. We provide a comprehensive and well-organized review....."
        ]
      },
      {
        title: "Manuscript",
        icon: "✂️",
        description: "Our company provides expert manuscript services, assisting with everything from structuring to editing and polishing your academic work. We ensure clarity....",
        details: [
          "Our manuscript services guide you through the entire preparation process, from structuring to final edits. We focus on ensuring clarity, coherence, and adherence to publication standards, helping you refine your manuscript for submission to journals or other academic platforms."
        ]
      },
      {
        title: "Research Methodology",
        icon: "💻",
        description: "Our company offers expert research methodology services, assisting with the design and execution of research plans, data collection techniques....",
        details: [
          "Our team helps you design effective research methodologies, including data collection techniques, analysis strategies, and statistical methods. We ensure your research plan is sound and meets academic standards, helping you achieve accurate and reliable results."
        ]
      },

];

const WritingServices: React.FC = () => {
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);

  return (
    <div className="p-3 sm:p-6 md:p-8 bg-[#f3f4f6]">
      {/* Header section with improved spacing */}
      <div className="text-center mb-6 sm:mb-8 md:mb-12">
        <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-950">
          Professional Assignment{" "}
          <span
            style={{
              background: "linear-gradient(-96deg, rgb(255 255 255 / 0%) 0%, rgb(255 249 220 / 0%) 28%, rgb(253, 103, 16) 100%, rgb(255 255 255 / 0%) 100%)",
              padding: "0px 5px",
            }}
          >
            Writing & Academic Support
          </span>
          Services
        </h1>
        <p className="text-sm sm:text-base md:text-lg text-gray-600 mt-2">
          Choose from our wide range of professional writing services
        </p>
      </div>

      {/* Card grid with improved spacing and responsiveness */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {topics.map((topic, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 p-4 flex flex-col min-h-[250px] sm:min-h-[280px]"
          >
            <div className="text-3xl sm:text-4xl mb-2 sm:mb-3 text-center">
              {topic.icon}
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#234152] text-center">
              {topic.title}
            </h3>
            <p className="text-[#234152] text-sm leading-relaxed text-center flex-grow overflow-hidden line-clamp-4 sm:line-clamp-5">
              {topic.description}
            </p>
            <button
              onClick={() => setSelectedTopic(topic)}
              className="w-full mt-3 sm:mt-4 py-2 px-4 bg-[#234152] text-white rounded hover:bg-orange-600 transition-colors duration-300 text-sm"
            >
              Learn More
            </button>
          </div>
        ))}
      </div>

      {/* Modal with improved mobile styling */}
      {selectedTopic && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <span>{selectedTopic.icon}</span>
                  <span>{selectedTopic.title}</span>
                </h2>
                <button
                  onClick={() => setSelectedTopic(null)}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <ul className="space-y-3">
                {selectedTopic.details.map((detail, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                    <span className="text-gray-700 text-sm sm:text-base">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WritingServices;